<template>
  <v-container fluid class="futuristic-panel">
    <v-card flat class="futuristic-card">
      <!-- Main tab (optional) – we directly show partner type tabs -->
      <v-tabs v-model="partnerTypeTab" slider-color="primary" class="type-tabs" v-if="partnerTypes.length > 0">
        <v-tab v-for="(type, index) in partnerTypes" :key="'type-' + index" ripple>
          {{ type.label }} ({{ getFilteredPartners(type.value).length }})
        </v-tab>

        <v-tab-item v-for="(type, index) in partnerTypes" :key="'type-content-' + index">
          <v-card-title class="card-header">
            <h4 class="earth-color">{{ type.label.toUpperCase() }} ({{ getFilteredPartners(type.value).length }})</h4>
            <v-spacer></v-spacer>
            <v-text-field append-icon="search"
                          :label="'Search ' + type.label"
                          single-line
                          hide-details
                          v-model="typeSearch[type.value]"
                          class="futuristic-input"
                          outlined></v-text-field>
          </v-card-title>
          <v-data-table :headers="columns"
                        :items="getFilteredPartners(type.value)"
                        :search="typeSearch[type.value]"
                        :rows-per-page-items="[10,25,{'text':'All','value':-1}]"
                        :disable-initial-sort="true"
                        :loading="$loadingTrue"
                        class="futuristic-table">
            <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
            <template v-slot:items="props">
              <td class="futuristic-table-cell">{{ props.item.type }}</td>
              <td class="futuristic-table-cell">{{ props.item.name }}</td>
              <td class="futuristic-table-cell">{{ props.item.email }}</td>
              <td class="futuristic-table-cell">{{ props.item.tel }}</td>
              <td class="futuristic-table-cell">{{ props.item.kra_pin_number }}</td>
              <td class="futuristic-table-cell">{{ props.item.customer_code }}</td>
              <td class="futuristic-table-cell">{{ props.item.customer_code_usd }}</td>
              <td class="futuristic-table-cell"
                  :style="{ backgroundColor: props.item.active_status === 'Active' ? '#44bd32' : '#fbc531' }">
                {{ props.item.active_status }}
              </td>
              <td class="has-text-right">
                <v-btn :depressed="true"
                       small
                       color="primary"
                       @click="viewMember(props.item.public_id)"
                       class="futuristic-btn">View Details</v-btn>
              </td>
            </template>
            <template v-slot:footer>
              <td :colspan="columns.length" class="footer-cell">
                <vue-json-to-csv :json-data="getFilteredPartners(type.value)"
                                 :csv-title="type.label + ' Partners List'"
                                 :labels="{
                    type: { title: 'Partner Type' },
                    name: { title: 'Partner Name' },
                    conservation_fee_discount: { title: 'Conservation Fee Discount' },
                    customer_code: { title: 'Customer Code (KES)' },
                    customer_code_usd: { title: 'Customer Code (USD)' },
                    email: { title: 'Email' },
                    kra_pin_number: { title: 'KRA Pin Number' },
                    tel: { title: 'Phone Number' },
                    active_status: { title: 'Partner Status' }
                  }">
                  <v-btn depressed color="primary" class="futuristic-btn">
                    <b>Download CSV</b>
                  </v-btn>
                </vue-json-to-csv>
              </td>
            </template>
            <v-alert slot="no-results" :value="true" color="error" icon="warning" class="futuristic-alert">
              Your search for "{{ typeSearch[type.value] }}" found no results.
            </v-alert>
            <v-alert slot="no-data" :value="true" color="info" icon="info" class="futuristic-alert">
              No {{ type.label.toLowerCase() }} partners found.
            </v-alert>
          </v-data-table>
        </v-tab-item>
      </v-tabs>

      <!-- Fallback when no partner types are detected -->
      <div v-else>
        <v-card-title class="card-header">
          <h4 class="earth-color">ALL PARTNERS ({{ allPartners.length }})</h4>
          <v-spacer></v-spacer>
          <v-text-field append-icon="search"
                        label="Search"
                        single-line
                        hide-details
                        v-model="search"
                        class="futuristic-input"
                        outlined></v-text-field>
        </v-card-title>
        <v-data-table :headers="columns"
                      :items="allPartners"
                      :search="search"
                      :rows-per-page-items="[10,25,{'text':'All','value':-1}]"
                      :disable-initial-sort="true"
                      :loading="$loadingTrue"
                      class="futuristic-table">
          <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
          <template v-slot:items="props">
            <td class="futuristic-table-cell">{{ props.item.type }}</td>
            <td class="futuristic-table-cell">{{ props.item.name }}</td>
            <td class="futuristic-table-cell">{{ props.item.email }}</td>
            <td class="futuristic-table-cell">{{ props.item.tel }}</td>
            <td class="futuristic-table-cell">{{ props.item.kra_pin_number }}</td>
            <td class="futuristic-table-cell">{{ props.item.customer_code }}</td>
            <td class="futuristic-table-cell">{{ props.item.customer_code_usd }}</td>
            <td class="futuristic-table-cell"
                :style="{ backgroundColor: props.item.active_status === 'Active' ? '#44bd32' : '#fbc531' }">
              {{ props.item.active_status }}
            </td>
            <td class="has-text-right">
              <v-btn :depressed="true"
                     small
                     color="primary"
                     @click="viewMember(props.item.public_id)"
                     class="futuristic-btn">View Details</v-btn>
            </td>
          </template>
          <template v-slot:footer>
            <td :colspan="columns.length" class="footer-cell">
              <vue-json-to-csv :json-data="allPartners"
                               csv-title="Partners List"
                               :labels="{
                  type: { title: 'Partner Type' },
                  name: { title: 'Partner Name' },
                  conservation_fee_discount: { title: 'Conservation Fee Discount' },
                  customer_code: { title: 'Customer Code (KES)' },
                  customer_code_usd: { title: 'Customer Code (USD)' },
                  email: { title: 'Email' },
                  kra_pin_number: { title: 'KRA Pin Number' },
                  tel: { title: 'Phone Number' },
                  active_status: { title: 'Partner Status' }
                }">
                <v-btn depressed color="primary" class="futuristic-btn">
                  <b>Download CSV</b>
                </v-btn>
              </vue-json-to-csv>
            </td>
          </template>
          <v-alert slot="no-results" :value="true" color="error" icon="warning" class="futuristic-alert">
            Your search for "{{ search }}" found no results.
          </v-alert>
          <v-alert slot="no-data" :value="true" color="info" icon="info" class="futuristic-alert">
            No partners found.
          </v-alert>
        </v-data-table>
      </div>
    </v-card>
  </v-container>
</template>

<script>
  import VueJsonToCsv from 'vue-json-to-csv'

  export default {
    components: { VueJsonToCsv },
    data() {
      return {
        partnerTypeTab: 0,
        search: '',
        typeSearch: {},
        columns: [
          {
            text: 'Partner Type',
            value: 'type'
          },
          {
            text: 'Partner Name',
            value: 'name'
          },
          {
            text: 'Partner Email',
            value: 'email'
          },
          {
            text: 'Partner Telephone',
            value: 'tel'
          },
          {
            text: 'KRA Pin Number',
            value: 'kra_pin_number'
          },
          {
            text: 'Customer Code (KES)',
            value: 'customer_code'
          },
          {
            text: 'Customer Code (USD)',
            value: 'customer_code_usd'
          },
          {
            text: 'Partner Status',
            value: 'active_status'
          },
          {
            text: 'Action',
            value: ''
          }
        ]
      }
    },
    computed: {
      partnerTypes() {
        const types = new Set()
        this.allPartners.forEach(partner => {
          if (partner.type) {
            const typeStr = String(partner.type).trim()
            if (typeStr) types.add(typeStr)
          }
        })
        const typeArray = Array.from(types).filter(t => t && t !== '')
        typeArray.sort()

        // Initialize search objects for each type
        const result = typeArray.map(type => ({
          label: this.formatTypeLabel(type),
          value: type
        }))

        // Ensure typeSearch has entries for all types
        result.forEach(type => {
          if (!this.typeSearch[type.value]) {
            this.$set(this.typeSearch, type.value, '')
          }
        })

        return result
      }
    },
    created() {
      this.$store.dispatch('get_all_partners')
    },
    methods: {
      viewMember(publicId) {
        this.$router.push({
          name: 'Partner',
          params: {
            partner_id: publicId
          }
        })
      },
      formatTypeLabel(type) {
        return String(type)
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      },
      getFilteredPartners(type) {
        if (type === 'all') {
          return this.allPartners
        }
        return this.allPartners.filter(partner => {
          const partnerType = partner.type ? String(partner.type).trim() : ''
          return partnerType === type
        })
      }
    },
    watch: {
      partnerTypes: {
        handler(newTypes) {
          newTypes.forEach(type => {
            if (!this.typeSearch[type.value]) {
              this.$set(this.typeSearch, type.value, '')
            }
          })
        },
        immediate: true
      }
    }
  }
</script>

<style scoped>
  .futuristic-panel {
    background: #f5f7fa;
    padding: 16px;
    border-radius: 8px;
  }

  .futuristic-card {
    border-radius: 8px;
    background: #ffffff;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    margin-bottom: 16px;
  }

    .futuristic-card:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    }

  .card-header {
    background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
    padding: 16px;
    border-radius: 8px 8px 0 0;
  }

  .earth-color {
    color: #4caf50;
    font-weight: 600;
    margin: 0;
  }

  .futuristic-divider {
    border: 0;
    height: 1px;
    background: linear-gradient(to right, rgba(76, 175, 80, 0.2), rgba(76, 175, 80, 0.8), rgba(76, 175, 80, 0.2));
    margin: 8px 0 16px;
  }

  .futuristic-input > > > .v-input__slot {
    border-radius: 8px;
    background: #f5f7fa;
    margin-bottom: 16px;
  }

  .futuristic-input > > > .v-text-field__slot input {
    color: #333;
  }

  .futuristic-table {
    background: #f5f7fa;
    border-radius: 8px;
  }

    .futuristic-table > > > .v-datatable__thead th {
      color: #4caf50;
      font-weight: 600;
      background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
    }

  .futuristic-table-cell {
    color: #333;
    padding: 12px;
  }

  .futuristic-btn {
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    transition: all 0.2s ease;
    margin: 4px;
  }

    .futuristic-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

  .futuristic-alert {
    border-radius: 8px;
    background: #ffebee;
    color: #c62828;
  }

  .footer-cell {
    padding: 16px;
    text-align: center;
  }

  .type-tabs .v-tabs__bar {
    background: linear-gradient(45deg, #f5f7fa, #e8eaf6);
    margin: 0 8px;
    border-radius: 8px 8px 0 0;
  }

  .type-tabs .v-tabs__item {
    font-size: 0.85rem;
    font-weight: 500;
    color: #4caf50;
    text-transform: none;
  }

  .type-tabs .v-tabs__item--active {
    background: rgba(76, 175, 80, 0.1);
  }

  @media (max-width: 960px) {
    .futuristic-panel {
      padding: 12px;
    }

    .card-header {
      padding: 12px;
    }

    .futuristic-btn {
      margin: 2px;
      font-size: 0.9rem;
    }

    .futuristic-table-cell {
      padding: 8px;
    }

    .type-tabs .v-tabs__item {
      font-size: 0.8rem;
      padding: 0 8px;
    }
  }

  @media (max-width: 600px) {
    .futuristic-card {
      border-radius: 6px;
    }

    .card-header {
      border-radius: 6px 6px 0 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .futuristic-input > > > .v-input__slot {
      border-radius: 6px;
    }

    .futuristic-btn {
      border-radius: 6px;
      width: 100%;
      margin: 4px 0;
    }

    .futuristic-table > > > .v-datatable__thead th {
      font-size: 0.9rem;
    }

    .futuristic-table-cell {
      font-size: 0.85rem;
      padding: 6px;
    }

    .futuristic-table table {
      min-width: 700px;
    }

    .type-tabs .v-tabs__wrapper {
      overflow-x: auto;
    }

    .type-tabs .v-tabs__container {
      min-width: 500px;
    }

    .type-tabs .v-tabs__item {
      font-size: 0.75rem;
      padding: 0 6px;
      min-width: auto;
    }
  }
</style>
